import type { SetStateAction } from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

import VNProgressModal from '@/components/VNProgressModal/VNProgressModal';

export const ProgressProviderContext = createContext({
  setProgress: (_p: SetStateAction<number | boolean>, _s?: string) => {
    //
  },
  loading: false,
});
export const useProgress = () => useContext(ProgressProviderContext);

const defaultStatus = 'Loading...';

type Props = {
  children: React.ReactNode;
};

/**
 * if progress === 100 or progress === false, the modal will not be shown
 * if progress === -1 or progress === true, the modal will show indeterminate progress
 */

const ProgressProvider = ({ children }: Props) => {
  const [progress, setProgressValue] = useState<number | boolean>(false);
  const [status, setStatus] = useState(defaultStatus);

  const setProgress = useCallback((newProgress: SetStateAction<number | boolean>, newStatus?: string) => {
    setProgressValue(newProgress);
    if (!newStatus && ((typeof newProgress === 'number' && (newProgress <= 0 || newProgress === 100)) || !newProgress)) {
      // reset the status message
      setStatus(defaultStatus);
    } else if (newStatus) {
      setStatus(newStatus);
    }
  }, []);

  const value = useMemo(
    () => ({
      setProgress,
      loading: typeof progress === 'boolean' ? progress : progress < 100,
    }),
    [setProgress, progress]
  );

  return (
    <>
      {value.loading && (
        <VNProgressModal
          variant={progress === true || progress === -1 ? 'indeterminate' : 'determinate'}
          progress={typeof progress === 'boolean' ? -1 : progress}
          status={status}
        />
      )}
      <ProgressProviderContext.Provider value={value}>{children}</ProgressProviderContext.Provider>
    </>
  );
};

export default ProgressProvider;
